'use client';

import { ChangeEvent } from 'react';
import clsx from 'clsx';
import { Component } from '@headless-workspace/typings';
import { IconComponentType, PropsWithClassName, PropsWithFontFamily } from '../../props';
import { GlowIcon } from '../icon';
import { GlowTextBody } from '../typography';

type LabelPill = {
    id: string;
    label: string;
    icon?: IconComponentType;
};

type ImagePill = {
    id: string;
    image: IconComponentType;
};

export type Pill = LabelPill | ImagePill;

export type GlowPillProps = Partial<PropsWithFontFamily> &
    PropsWithClassName & {
        type: 'radio' | 'checkbox';
        content: Pill;
        checked: boolean;
        onChange: (id: string, checked: boolean) => void;
        wrapperClassName?: string;
        inputName?: string;
    };

export const GlowPill: Component<GlowPillProps> = ({
    type,
    content,
    checked,
    inputName,
    onChange,
    fontFamily = 'brand',
    className = 'before:rounded-infinity',
    wrapperClassName,
}) => {
    const onChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        onChange(content.id, event.target.checked);
    };

    const isLabelPill = (content: Pill): content is LabelPill => {
        return 'label' in content;
    };

    return (
        <div
            className={clsx(
                // CSS to create an “internal border” => prevents offset
                'inline-flex relative before:absolute',
                'pointer-events-none',
                'before:w-full before:h-full',
                'h-[3rem]',
                className,
            )}
            tabIndex={0}
            role={type}
            aria-checked={checked}
        >
            <label
                className={clsx(
                    'flex flex-row',
                    'px-1 py-0.75',
                    'pointer-events-auto',
                    'rounded-infinity outline outline-solid',
                    checked
                        ? 'bg-surface-primary outline-bold outline-border-selected'
                        : 'bg-background-neutral outline-medium outline-border-primary',
                    'hover:bg-surface-neutral-hover active:bg-surface-neutral-pressed',
                    'has-[:focus-visible]:outline -thin has-[:focus-visible]:border has-[:focus-visible]:rounded-infinity',
                    wrapperClassName,
                )}
            >
                {isLabelPill(content) ? (
                    <>
                        {content.icon && <GlowIcon Icon={content.icon} type={'medium'} />}
                        <input
                            type={type}
                            name={inputName}
                            className={'sr-only'}
                            checked={checked}
                            onChange={onChangeHandler}
                            value={content.id}
                        />
                        <GlowTextBody text={content.label} TagName={'span'} fontFamily={fontFamily} />
                    </>
                ) : (
                    <>
                        <input
                            type={type}
                            name={inputName}
                            className={'sr-only'}
                            checked={checked}
                            onChange={onChangeHandler}
                            value={content.id}
                        />
                        <GlowIcon Icon={content.image} type={'fullSize'} alt={content.id} />
                    </>
                )}
            </label>
        </div>
    );
};
